import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, Button, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grow, Icon } from '@mui/material';
import { Payment, CheckCircle, Speed, DeviceHub, SupportAgent, VerifiedUser } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../App';

const plans = [
  { name: 'Free', price: '$0', device_limit: 10, data_limit_mb: 1000, color: '#94a3b8', features: ['10 Nodes', '1GB Data Stream', 'Community Gateway'] },
  { name: 'Basic', price: '$29/mo', device_limit: 100, data_limit_mb: 10000, color: '#00B4DB', features: ['100 Nodes', '10GB Data Stream', 'Dedicated Link'] },
  { name: 'Pro', price: '$99/mo', device_limit: 1000, data_limit_mb: 100000, color: '#47cf73', features: ['1000 Nodes', '100GB Data Stream', 'Priority Logic', 'Geo-Spatial AI'] },
  { name: 'Enterprise', price: 'Custom', device_limit: 10000, data_limit_mb: 1000000, color: '#f50057', features: ['Unlimited Nodes', '1TB Data Stream', 'Global 24/7 Ops', 'White-Label'] },
];

function SubscriptionPage() {
  const { token } = useAuth();
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    fetchSubscription();
  }, [token]);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(
        '/api/v1/auth/subscription',
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubscription(response.data);
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    }
  };

  const currentPlan = plans.find(p => p.name.toLowerCase() === subscription?.plan_type) || plans[0];

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: -1 }}>
          Subscription Capacity
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Configure your network throughput and node limits
        </Typography>
      </Box>

      {subscription && (
        <Grow in>
          <Paper className="glass-card" sx={{ p: 0, mb: 6, overflow: 'hidden', borderLeft: `8px solid ${currentPlan.color} !important` }}>
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'rgba(0,0,0,0.02)' }}>
              <Box>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 900, letterSpacing: 2 }}>CURRENT LAYER</Typography>
                <Typography variant="h3" sx={{ fontWeight: 900, color: currentPlan.color }}>{currentPlan.name.toUpperCase()}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Chip
                  label={subscription.status.toUpperCase()}
                  size="small"
                  sx={{ bgcolor: 'rgba(71, 207, 115, 0.1)', color: '#47cf73', fontWeight: 900, borderRadius: 1 }}
                />
                <Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>{currentPlan.price}</Typography>
              </Box>
            </Box>
            <Box sx={{ p: 4 }}>
              <Grid container spacing={4}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <DeviceHub sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>NODE CAPACITY</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>{subscription.device_limit}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Speed sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>THROUGHPUT</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>{subscription.data_limit_mb} MB</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <VerifiedUser sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>SECURITY CORE</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>ENCRYPTED</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <SupportAgent sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>PROTOCOL OPS</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>ACTIVE 24/7</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grow>
      )}

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>Capacity Scaling</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>Upgrade your operational threshold to scale your fleet.</Typography>
      </Box>

      <Grid container spacing={4}>
        {plans.map((plan, idx) => (
          <Grid item xs={12} sm={6} md={3} key={plan.name}>
            <Grow in timeout={1500 + (idx * 200)}>
              <Card className="glass-card" sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderTop: `6px solid ${plan.color} !important` }}>
                <CardContent sx={{ p: 4, flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>{plan.name}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: plan.color, mb: 3 }}>{plan.price}</Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {plan.features.map((feature) => (
                      <Box key={feature} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CheckCircle sx={{ fontSize: 16, color: plan.color }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>{feature}</Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
                <Box sx={{ p: 4, pt: 0 }}>
                  <Button
                    fullWidth
                    variant={plan.name.toLowerCase() === subscription?.plan_type ? 'outlined' : 'contained'}
                    disabled={plan.name.toLowerCase() === subscription?.plan_type}
                    sx={{
                      borderRadius: 3,
                      fontWeight: 900,
                      fontSize: '0.9rem',
                      py: 1.5,
                      bgcolor: plan.name.toLowerCase() === subscription?.plan_type ? 'transparent' : plan.color,
                      '&:hover': { bgcolor: plan.name.toLowerCase() === subscription?.plan_type ? 'transparent' : plan.color, opacity: 0.9 }
                    }}
                  >
                    {plan.name.toLowerCase() === subscription?.plan_type ? 'ACTIVE CORE' : 'INITIALIZE'}
                  </Button>
                </Box>
              </Card>
            </Grow>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default SubscriptionPage;
